
import { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_wg5fvsj';
const EMAILJS_TEMPLATE_ID = 'template_p3uaz0m';
const EMAILJS_PUBLIC_KEY = 'V7jp-ceM6kWiC5Bnl';

export default function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    property_type: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional
    const phoneRegex = /^[+]?[0-9\s\-()]{10,}$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!validateEmail(formData.email)) {
      setSubmitStatus('error');
      return;
    }

    // Validate phone if provided
    if (!validatePhone(formData.phone)) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send email using EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Non fourni',
        service: formData.service,
        property_type: formData.property_type || 'Non spécifié',
        message: formData.message,
        to_email: 'info@netyxpro.com'
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        property_type: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <form ref={formRef} onSubmit={handleSubmit} id="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
              Nom complet *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
              placeholder="Votre nom"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
              placeholder="votre@email.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
              placeholder="01 23 45 67 89"
            />
          </div>
          
          <div>
            <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
              Service souhaité *
            </label>
            <div className="relative">
              <select
                id="service"
                name="service"
                required
                value={formData.service}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm pr-8 appearance-none cursor-pointer"
              >
                <option value="">Sélectionnez un service</option>
                <option value="menage-domicile">Ménage à domicile</option>
                <option value="lessive-repassage">Lessive & repassage</option>
                <option value="lavage-vitres">Lavage de vitres</option>
                <option value="courses-personnelles">Courses personnelles</option>
                <option value="bureaux-espaces-travail">Bureaux & espaces de travail</option>
                <option value="commerces-boutiques">Commerces & boutiques</option>
                <option value="desinfection-professionnelle">Désinfection professionnelle</option>
                <option value="livraison-entreprise">Livraison entreprise</option>
                <option value="autre">Autre</option>
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="property_type" className="block text-sm font-medium text-gray-300 mb-2">
            Type de bien/structure
          </label>
          <div className="relative">
            <select
              id="property_type"
              name="property_type"
              value={formData.property_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm pr-8 appearance-none cursor-pointer"
            >
              <option value="">Sélectionnez le type</option>
              <option value="appartement">Appartement</option>
              <option value="maison">Maison</option>
              <option value="bureau-pme">Bureau PME</option>
              <option value="bureau-grande-entreprise">Bureau grande entreprise</option>
              <option value="commerce">Commerce</option>
              <option value="industrie">Industrie</option>
              <option value="etablissement-medical">Établissement médical</option>
              <option value="autre">Autre</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={4}
            maxLength={500}
            value={formData.message}
            onChange={handleInputChange}
            className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm resize-none"
            placeholder="Décrivez vos besoins, fréquence souhaitée, surface approximative..."
          />
          <div className="text-xs text-gray-400 mt-1">
            {formData.message.length}/500 caractères
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || formData.message.length > 500}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Envoi en cours...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line mr-2"></i>
              Demander un Devis Gratuit
            </>
          )}
        </button>

        {submitStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-600 text-white rounded-lg text-sm">
            <i className="ri-check-line mr-2"></i>
            Merci ! Votre demande a été envoyée. Nous vous recontacterons sous 24h.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-4 p-3 bg-red-600 text-white rounded-lg text-sm">
            <i className="ri-error-warning-line mr-2"></i>
            Une erreur s'est produite. Veuillez réessayer ou nous appeler directement.
          </div>
        )}
      </form>
    </div>
  );
}
